import React from 'react';
import { MapPin, Phone, Mail, Clock, Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

const Footer = () => {
  const { t } = useLanguage();
  const mapsUrl = "https://maps.app.goo.gl/WCYAVc7mXX32VhuS7";
  const appleMapsUrl = "https://maps.apple.com/place?q=Snack+DJOCO&ll=30.927888,-6.900340&t=h";
  const googleReviewUrl = "https://search.google.com/local/writereview?placeid=ChIJORig4wIRuw0R41DhXm_9HEk";
  const address = "Av. Al-Maghreb Al-Arabi, Ouarzazate 45000";

  const handleLocationClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    window.open(isIOS ? appleMapsUrl : mapsUrl, '_blank');
  };

  const handleReviewClick = () => {
    window.open(googleReviewUrl, '_blank');
  };

  // NOTE: the /developer route does not exist (its route + link were removed
  // from App.tsx because nothing navigated to it); do not re-add a footer
  // link unless the route is re-enabled.

  return (
    <footer className="bg-secondary text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* About Section */}
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold text-primary font-heading mb-4">DJOCO</h3>
            <p className="text-gray-300 mb-4 text-sm font-body">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-2 text-gray-300 text-sm mb-6">
              <Clock size={16} className="text-primary" />
              <div className="font-body">
                <p>{t('footer.hours.weekdays')}</p>
                <p>{t('footer.hours.weekends')}</p>
              </div>
            </div>
            {/* Review Button */}
            <button
              onClick={handleReviewClick}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all duration-300"
            >
              <Star className="w-4 h-4" />
              <span>Review our store</span>
              <ExternalLink size={16} />
            </button>
          </div>
          
          {/* Contact Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-primary font-heading">{t('footer.contact')}</h3>
            <div className="space-y-4">
              <a 
                href="tel:+212632302829"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = 'tel:+212632302829';
                }}
                className="flex items-center gap-2 text-gray-300 hover:text-primary transition-colors font-body text-sm group"
              >
                <Phone size={16} className="group-hover:scale-110 transition-transform" />
                <span>+212 632 30 28 29</span>
              </a>
              <a 
                href="mailto:Yacoub.faik@Gmail.com" 
                className="flex items-center gap-2 text-gray-300 hover:text-primary transition-colors font-body text-sm group"
              >
                <Mail size={16} className="group-hover:scale-110 transition-transform" />
                <span>Yacoub.faik@Gmail.com</span>
              </a>
              <button 
                onClick={handleLocationClick}
                className="flex items-center gap-2 text-gray-300 hover:text-primary transition-colors font-body text-sm text-left w-full group"
              >
                <MapPin size={16} className="group-hover:scale-110 transition-transform" />
                <span>{address}</span>
              </button>
            </div>
          </div>
          
          {/* Map Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-primary font-heading">{t('footer.location')}</h3>
            <div className="h-48 bg-gray-800 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300">
              <iframe
                title="DJOCO Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3425.772830489335!2d-6.918300323439435!3d30.919737681576675!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xdbb1102e3a01839%3A0x491cfd6f5ee150e3!2sSnack%20DJOCO!5e0!3m2!1sfr!2sma!4v1710285847955!5m2!1sfr!2sma"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="hover:opacity-90 transition-opacity"
              />
            </div>
          </div>
        </div>
        
        {/* Bottom Section */}
        <div className="mt-12 pt-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()}{' '}
              <span className="text-primary">
                @UPSICAST
              </span>
            </p>
            <Link 
              to="/admin"
              className="text-gray-400 hover:text-primary text-sm transition-colors font-body"
            >
              {t('footer.admin')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;